import React, { useState } from "react";
import { Card, Empty, Field, Kpi, PageHeading, Rate, RowsSkeleton, SearchInput, Select, Table, Toggle, Workspace } from "../ui";
import { useFilters } from "../filters";
import { NAMES, workspaceLabel } from "../data";
import { ago, fmt, statusOf } from "../format";
import { ACTIVE_SINCE, MIN_REQUESTS, SUCCESS_BANDS, Toolbar, activeWithin, countActive, inBand, matches } from "./lists";

const DEFAULTS = { q: "", only: {}, minRequests: "", success: "", activeSince: "" };

/** Every workspace that used the agent in the period. A row opens the whole dashboard filtered to it. */
export default function Workspaces({ accounts: data }) {
  const { set, f: global } = useFilters();
  const [f, setF] = useState(DEFAULTS);
  const on = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const only = (k) => (v) => setF((x) => ({ ...x, only: { ...x.only, [k]: v } }));
  const all = data?.accounts ?? [];
  const rows = all
    .filter((a) => !global.ws || a.accountId === global.ws)
    .filter((a) => matches(f.q, workspaceLabel(a.accountId), NAMES.accounts.get(a.accountId)?.slug, a.accountId))
    .filter((a) => !f.only.failed || a.failed > 0)
    .filter((a) => !f.only.friction || a.friction > 0)
    .filter((a) => !f.only.saved || a.recordsSaved > 0)
    .filter((a) => !f.only.open || a.open > 0)
    .filter((a) => !f.minRequests || a.runs >= Number(f.minRequests))
    .filter((a) => inBand(a.successRate, f.success))
    .filter((a) => activeWithin(a.lastActive, f.activeSince));
  const totals = all.reduce((s, a) => ({ runs: s.runs + a.runs, failed: s.failed + a.failed, saved: s.saved + (a.recordsSaved ?? 0) }), { runs: 0, failed: 0, saved: 0 });

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <PageHeading title="Workspaces">Every workspace that used the agent in this period. Click one to see the whole dashboard for it.</PageHeading>

      <div className="aa-grid aa-grid-kpi">
        <Kpi loading={!data} label="Active workspaces" value={fmt(all.length)} />
        <Kpi loading={!data} label="With a failed request" value={fmt(all.filter((a) => a.failed > 0).length)} foot={`${fmt(totals.failed)} failed requests`} />
        <Kpi loading={!data} label="Saved records" value={fmt(all.filter((a) => a.recordsSaved > 0).length)} foot={`${fmt(totals.saved)} records in total`} />
        <Kpi loading={!data} label="Requests per workspace" value={all.length ? fmt(totals.runs / all.length, 1) : "—"} foot="on average" />
      </div>

      <Toolbar shown={rows.length} total={all.length} noun="workspaces" active={countActive(f)} onClear={() => setF(DEFAULTS)}>
        <Field label="Search">
          <SearchInput value={f.q} onChange={on("q")} placeholder="Workspace name or id" />
        </Field>
        <Field label="Requests">
          <Select value={f.minRequests} onChange={on("minRequests")} options={MIN_REQUESTS} />
        </Field>
        <Field label="Success">
          <Select value={f.success} onChange={on("success")} options={SUCCESS_BANDS} />
        </Field>
        <Field label="Last active">
          <Select value={f.activeSince} onChange={on("activeSince")} options={ACTIVE_SINCE} />
        </Field>
        <Field label="Only workspaces that">
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Toggle on={f.only.failed} onChange={only("failed")}>Had failures</Toggle>
            <Toggle on={f.only.friction} onChange={only("friction")}>Got stuck</Toggle>
            <Toggle on={f.only.open} onChange={only("open")}>Have open requests</Toggle>
            <Toggle on={f.only.saved} onChange={only("saved")}>Saved records</Toggle>
          </div>
        </Field>
      </Toolbar>

      <Card flush>
        {!data ? (
          <RowsSkeleton />
        ) : rows.length ? (
          <Table
            defaultSort={{ key: "runs", dir: "desc" }}
            onRow={(r) => set({ ws: r.accountId, tab: "overview" })}
            columns={[
              { key: "accountId", label: "Workspace", sort: (r) => workspaceLabel(r.accountId), render: (r) => <Workspace id={r.accountId} sub /> },
              { key: "users", label: "People", align: "right", render: (r) => fmt(r.users) },
              { key: "conversations", label: "Conversations", align: "right", render: (r) => fmt(r.conversations) },
              { key: "runs", label: "Requests", align: "right", render: (r) => fmt(r.runs) },
              { key: "successRate", label: "Success", align: "right", render: (r) => <Rate value={r.successRate} status={statusOf(r.successRate, { good: 0.9, bad: 0.7 })} /> },
              { key: "failed", label: "Failed", align: "right", render: (r) => <span style={{ color: r.failed ? "var(--aa-bad-ink)" : "var(--aa-faint)", fontWeight: r.failed ? 600 : 400 }}>{fmt(r.failed)}</span> },
              { key: "open", label: "Open", align: "right", render: (r) => fmt(r.open) },
              { key: "friction", label: "Stuck", align: "right", render: (r) => <span style={{ color: r.friction ? "var(--aa-warn-ink)" : "var(--aa-faint)" }}>{fmt(r.friction)}</span> },
              { key: "recordsSaved", label: "Records", align: "right", render: (r) => fmt(r.recordsSaved) },
              { key: "credits", label: "Credits", align: "right", render: (r) => fmt(r.credits) },
              { key: "lastActive", label: "Last active", nowrap: true, sort: (r) => new Date(r.lastActive).getTime(), render: (r) => <span title={new Date(r.lastActive).toLocaleString()}>{ago(r.lastActive)}</span> },
            ]}
            rows={rows.map((r) => ({ ...r, id: r.accountId }))}
          />
        ) : (
          <Empty icon="workspaces" title={all.length ? "No workspace matches these filters" : "No workspace used the agent in this period"} />
        )}
      </Card>
      {rows.length > 0 && <div style={{ fontSize: 12, color: "var(--aa-muted)", marginTop: -8 }}>Success is over requests that ended — one waiting on the user is not a failure.</div>}
    </div>
  );
}
